import axios from 'axios';
import API_BASE_URL from './config/api';

const currentUrl = API_BASE_URL;

const createRequestId = () => {
	if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}

	return `escala-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const escalaScopeSuffix = (scope) =>
	['enfermagem', 'multiprofissional', 'administrativa'].includes(scope) ? `${scope}/` : '';

const buildEscalaUrl = (path, scope) => {
	const normalizedPath = String(path || '').replace(/^\//, '');
	return `${currentUrl}/escala/${escalaScopeSuffix(scope)}${normalizedPath}`;
};

const buildEscalaActionUrl = ({ href, scope, fallbackPath }) => {
	const normalizedHref = String(href || '').trim();
	if (!normalizedHref) return buildEscalaUrl(fallbackPath, scope);
	const base = new URL(currentUrl || '/', window.location.origin);
	const target = new URL(
		normalizedHref,
		`${base.href.replace(/\/+$/, '')}/escala/${escalaScopeSuffix(scope)}`,
	);
	if (
		target.origin !== base.origin ||
		!target.pathname.startsWith('/escala/') ||
		target.username || target.password
	) {
		throw integrationError('ACAO_FORA_DA_API', 'A ação recebida está fora da API da Escala. Recarregue a tela.');
	}
	return target.href;
};

const authHeaders = (token) => ({
	// Token da SPA e sessão legada são alternativas por requisição.
	withCredentials: !token,
	headers: {
		...(token ? { Authorization: `Token ${token}` } : {}),
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'X-Request-ID': createRequestId(),
	},
});

const integrationError = (code, message) =>
	Object.assign(new Error(message), { integrationCode: code });

const assertActionAvailable = (action) => {
	if (action?.disponivel === false) {
		throw integrationError(
			action.motivo_codigo || 'ACAO_INDISPONIVEL',
			action.motivo || 'Esta ação não está disponível. Recarregue o estado da escala.',
		);
	}
};

// As ações estruturais são HATEOAS: além da rota e método, o backend pode
// declarar cabeçalhos fixos que precisam acompanhar a chamada. Valores que
// dependem da decisão (como If-Match e Idempotency-Key) continuam sendo
// adicionados pelo chamador, pois não podem ser inventados pelo cliente.
const getActionDeclaredHeaders = (action = {}) => {
	assertActionAvailable(action);
	const candidates = [
		action?.cabecalhos,
		action?.headers,
		action?.cabecalhos_obrigatorios,
	];

	return candidates.reduce((headers, candidate) => {
		if (!candidate || Array.isArray(candidate) || typeof candidate !== 'object') {
			return headers;
		}
		return { ...headers, ...candidate };
	}, {});
};

const getActionMethod = (action) => {
	assertActionAvailable(action);
	const method = String(action?.method || 'POST').toLowerCase();
	if (!['get', 'post', 'put', 'patch', 'delete'].includes(method)) {
		throw integrationError('METODO_ACAO_INVALIDO', 'O método da ação recebida não é suportado. Recarregue a tela.');
	}
	return method;
};

const executeActionRequest = (url, method, body, config) => {
	if (method === 'get') return axios.get(url, { ...config, params: compactParams(body || {}) });
	if (method === 'delete') return axios.delete(url, { ...config, data: body });
	return axios[method](url, body, config);
};

const normalizeEstabelecimentoParam = (value = '') => {
	if (value && typeof value === 'object') {
		return String(
			value?.id ||
				value?.cnes ||
				value?.cnes_estabelecimento ||
				value?.codigo ||
				value?.value ||
				'',
		).trim();
	}

	return String(value || '').trim();
};

const normalizeRequestParams = (params = {}) =>
	Object.fromEntries(
		Object.entries(params).map(([key, value]) => [
			key,
			key === 'estabelecimento'
				? normalizeEstabelecimentoParam(value)
				: value,
		]),
	);

const compactParams = (params = {}) =>
	Object.fromEntries(
		Object.entries(normalizeRequestParams(params)).filter(
			([, value]) =>
				value !== undefined && value !== null && String(value) !== '',
		),
	);

const normalizeContextoBodyEstabelecimento = (body = {}) => {
	if (!body?.contexto) return body;

	return {
		...body,
		contexto: {
			...body.contexto,
			estabelecimento:
				normalizeEstabelecimentoParam(body.contexto.estabelecimento) ||
				undefined,
		},
	};
};

// Corpo e precondição pertencem ao mesmo preview. Uma seleção local antiga
// nunca pode fornecer a revisão de um comando canônico mais recente.
const buildApplyRequest = ({ token, action, body, previewEtag, idempotencyKey, quotePreview = true }) => {
	const payload = action?.entrada ?? normalizeContextoBodyEstabelecimento(body);
	const declared = getActionDeclaredHeaders(action);
	const header = (name) => Object.entries(declared).find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1];
	const config = authHeaders(token);
	config.headers = { ...config.headers, ...declared };
	const setHeader = (name, value) => {
		Object.keys(config.headers).forEach((key) => {
			if (key.toLowerCase() === name.toLowerCase()) delete config.headers[key];
		});
		config.headers[name] = value;
	};
	const etag = payload?.preview_etag || header('If-Match') || previewEtag || body?.preview_etag;
	const key = payload?.chave_idempotencia || payload?.idempotency_key || header('Idempotency-Key') || idempotencyKey || body?.chave_idempotencia || body?.idempotency_key;
	if (etag) {
		const value = String(etag);
		setHeader('If-Match', quotePreview && !value.startsWith('"') ? `"${value}"` : value);
	}
	if (key) setHeader('Idempotency-Key', key);
	return { payload, config };
};

const buildError = (error, fallbackMessage) => {
	if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
		return { error: { code: 'REQUEST_CANCELED', canceled: true, message: '' } };
	}
	if (error?.integrationCode) {
		return { error: { code: error.integrationCode, message: error.message, detail: error.message } };
	}
	const responseData = error?.response?.data;
	const status = error?.response?.status;
	const responseHeaders = error?.response?.headers || {};
	const code =
		responseData?.codigo ||
		responseData?.code ||
		responseData?.erro?.codigo ||
		null;
	const detail =
		responseData?.detalhe ||
		responseData?.detail ||
		responseData?.mensagem ||
		responseData?.erro?.mensagem ||
		(responseData && typeof responseData === 'object'
			? responseData
			: fallbackMessage);

	return {
		error: {
			status,
			code,
			title: responseData?.titulo || responseData?.title || '',
			detail,
			message: responseData?.mensagem || responseData?.detail || fallbackMessage,
			retryable: responseData?.retryable === true,
			requestId:
				responseData?.request_id ||
				responseHeaders?.['x-request-id'] ||
				responseHeaders?.['X-Request-ID'] ||
				'',
			retryAfter:
				responseHeaders?.['retry-after'] ||
				responseHeaders?.['Retry-After'] ||
				null,
			issues: Array.isArray(responseData?.issues)
				? responseData.issues
				: Array.isArray(responseData?.erros)
					? responseData.erros
						: [],
			orientacao: responseData?.orientacao || null,
			campo_foco: responseData?.campo_foco || responseData?.orientacao?.campo_foco || null,
			acao_sugerida: responseData?.acao_sugerida || responseData?.orientacao?.acao_sugerida || null,
			data: responseData || null,
		},
	};
};

const unwrapList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.results)) return payload.results;
	return [];
};

const normalizeMesReferencia = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';

	const exactMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
	if (exactMatch?.[1]) return exactMatch[1];

	const parsed = new Date(raw);
	if (Number.isNaN(parsed.getTime())) return '';

	const year = parsed.getFullYear();
	const month = String(parsed.getMonth() + 1).padStart(2, '0');
	const day = String(parsed.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

const buildAmbienteContextoParams = (contexto = {}, modo) =>
	compactParams({
		// Contextos devolvidos pela API podem trazer a unidade como objeto;
		// endpoints de ambiente sempre recebem o seu identificador.
		estabelecimento: normalizeEstabelecimentoParam(
			contexto?.estabelecimento,
		),
		setor: contexto?.setor,
		mes_referencia: normalizeMesReferencia(contexto?.mes_referencia),
		vinculo_profissional_id:
			contexto?.vinculo_profissional_id || undefined,
		profissional_id: contexto?.profissional_id || undefined,
		modo,
	});

const normalizeEstabelecimentoCatalogo = (item = {}) => ({
	...item,
	id: item?.id || item?.cnes || item?.codigo || '',
	cnes: String(item?.cnes || item?.id || item?.codigo || ''),
	nome: String(
		item?.nome ||
			item?.nome_fantasia ||
			item?.razao_social ||
			item?.label ||
			item?.descricao ||
			'--',
	),
	label: String(
		item?.nome ||
			item?.nome_fantasia ||
			item?.razao_social ||
			item?.label ||
			item?.descricao ||
			'--',
	),
});

const normalizeSalaCatalogo = (sala = {}, setor = {}, tipoSecao = {}) => ({
	...sala,
	id: sala?.id,
	nome: sala?.nome || sala?.nome_exibicao || '-',
	tipo_secao: sala?.tipo_secao || tipoSecao?.id || setor?.tipo_secao || null,
	nome_tipo_secao:
		sala?.nome_tipo_secao ||
		tipoSecao?.nome ||
		setor?.nome_tipo_secao ||
		'',
	nome_setor: sala?.nome_setor || setor?.nome || setor?.nome_exibicao || '',
	nome_setor_exibicao:
		sala?.nome_setor_exibicao || setor?.nome_exibicao || setor?.nome || '',
});

const normalizeSetorCatalogo = (setor = {}, tipoSecao = {}) => ({
	...setor,
	id: setor?.id,
	nome: setor?.nome || setor?.nome_exibicao || '-',
	nome_exibicao: setor?.nome_exibicao || setor?.nome || '-',
	identificador:
		setor?.identificador ||
		setor?.codigo ||
		setor?.codigo_identificador ||
		setor?.identificador_escala ||
		setor?.escala_identificador ||
		'',
	tipo_secao: setor?.tipo_secao || tipoSecao?.id || null,
	nome_tipo_secao:
		setor?.nome_tipo_secao ||
		tipoSecao?.nome ||
		setor?.tipo_secao_nome ||
		'',
	andar: setor?.andar || '-',
	quantidade_leitos: setor?.quantidade_leitos ?? setor?.numero_leitos ?? null,
	quantidade_salas: setor?.quantidade_salas ?? null,
	salas: unwrapList(setor?.salas).map((sala) =>
		normalizeSalaCatalogo(sala, setor, tipoSecao),
	),
});

const normalizeTipoSecaoCatalogo = (tipoSecao = {}) => ({
	...tipoSecao,
	id: tipoSecao?.id,
	nome: tipoSecao?.nome || tipoSecao?.nome_tipo_secao || '-',
	setores: unwrapList(tipoSecao?.setores).map((setor) =>
		normalizeSetorCatalogo(setor, tipoSecao),
	),
});

const normalizeCatalogosEscalaSelects = (payload = {}) => {
	const tiposSecao = unwrapList(
		payload?.tipos_secao || payload?.tiposSecao || payload?.tipo_secao,
	).map(normalizeTipoSecaoCatalogo);

	const servicosLegacy = unwrapList(payload?.servicos).map((item) => ({
		...item,
		id: item?.id,
		nome: item?.nome || '-',
		setores: unwrapList(item?.setores).map((setor) =>
			normalizeSetorCatalogo(setor, item),
		),
	}));

	const servicos = tiposSecao.length > 0 ? tiposSecao : servicosLegacy;
	const setoresDiretos = unwrapList(payload?.setores).map((setor) =>
		normalizeSetorCatalogo(setor),
	);
	const setoresAninhados = servicos.flatMap((item) => item?.setores || []);
	const setores =
		setoresDiretos.length > 0 ? setoresDiretos : setoresAninhados;
	const salas = setores.flatMap((setor) => setor?.salas || []);
	const identificadores = unwrapList(payload?.identificadores).map(
		(item = {}) => ({
			...item,
			value: String(
				item?.identificador ||
					item?.codigo ||
					item?.valor ||
					item?.id ||
					'',
			),
			nome_exibicao: String(
				item?.nome_exibicao ||
					item?.['nome_exibição'] ||
					item?.nome ||
					item?.identificador ||
					item?.codigo ||
					item?.valor ||
					item?.id ||
					'--',
			),
			label: String(
				item?.nome_exibicao ||
					item?.['nome_exibição'] ||
					item?.nome ||
					item?.identificador ||
					item?.codigo ||
					item?.valor ||
					item?.id ||
					'--',
			),
			tipo_secao:
				item?.tipo_secao?.id ||
				item?.tipo_secao ||
				item?.tipoSecao ||
				null,
			setor_id:
				item?.setor?.id ||
				item?.setor_id ||
				item?.setor ||
				item?.id_setor ||
				null,
			quantidade_leitos:
				item?.quantidade_leitos ??
				item?.leitos ??
				item?.total_leitos ??
				null,
		}),
	);
	const regimes = unwrapList(payload?.regimes).map((item = {}) => ({
		...item,
		value: String(item?.codigo || item?.valor || item?.id || item || ''),
		label: String(
			item?.nome ||
				item?.label ||
				item?.codigo ||
				item?.valor ||
				item ||
				'',
		),
	}));
	const competenciaPadrao = payload?.competencia_padrao || null;
	const estabelecimentos = unwrapList(
		payload?.estabelecimentos ||
			payload?.unidades ||
			payload?.estabelecimento,
	).map(normalizeEstabelecimentoCatalogo);

	return {
		servicos,
		tiposSecao: servicos,
		estabelecimentos,
		identificadores,
		setores,
		salas,
		regimes,
		competenciaPadrao,
		categorias: unwrapList(payload?.categorias || []),
		especialidades: unwrapList(payload?.especialidades || []),
	};
};

const normalizeCardEscala = (
	card = {},
	celula = {},
	linha = {},
	setor = {},
) => ({
	...card,
	id: card?.id,
	tipo_item: card?.tipo_item || card?.rotulo_tipo || 'ALOCACAO',
	rotulo_tipo:
		card?.rotulo_tipo || card?.tipo_item || card?.tipo || 'Alocação',
	nome_profissional: card?.nome_profissional || card?.resumo?.titulo || '',
	hora_inicio: card?.hora_inicio || card?.resumo?.hora_inicio || '',
	hora_fim: card?.hora_fim || card?.resumo?.hora_fim || '',
	nome_especialidade:
		card?.nome_especialidade ||
		card?.resumo?.especialidade ||
		card?.especialidade ||
		'',
	descricao_vinculo:
		card?.descricao_vinculo || card?.resumo?.vinculo || card?.vinculo || '',
	observacao: card?.observacao || card?.resumo?.observacao || '',
	acoes: {
		pode_editar: card?.acoes?.pode_editar ?? true,
		pode_excluir: card?.acoes?.pode_excluir ?? true,
		pode_trocar: card?.acoes?.pode_trocar ?? false,
		bloqueio: card?.acoes?.bloqueio ?? null,
		...card?.acoes,
	},
	troca: card?.troca || card?.dados_edicao?.troca || null,
	dados_edicao: {
		...(card?.dados_edicao || {}),
		data: card?.dados_edicao?.data || celula?.data || card?.data || '',
		setor: card?.dados_edicao?.setor || setor?.id || null,
		sala: card?.dados_edicao?.sala || linha?.sala?.id || card?.sala || null,
		hora_inicio: card?.dados_edicao?.hora_inicio || card?.hora_inicio || '',
		hora_fim: card?.dados_edicao?.hora_fim || card?.hora_fim || '',
		observacao: card?.dados_edicao?.observacao || card?.observacao || '',
	},
});

const normalizeLinhaEscala = (linha = {}, colunas = [], setor = {}) => {
	const celulasNormalizadas = Array.isArray(linha?.celulas)
		? linha.celulas.map((celula = {}) => ({
				...celula,
				cards: Array.isArray(celula?.cards)
					? celula.cards.map((card) =>
							normalizeCardEscala(card, celula, linha, setor),
						)
					: [],
				quantidade_itens:
					celula?.quantidade_itens ?? celula?.cards?.length ?? 0,
				tem_itens:
					celula?.tem_itens ??
					(Array.isArray(celula?.cards) && celula.cards.length > 0),
			}))
		: [];

	if (celulasNormalizadas.length > 0) {
		return {
			...linha,
			sala: linha?.sala || null,
			faixa_horaria: linha?.faixa_horaria || {
				codigo: 'DIURNO',
				nome: 'Diurno',
			},
			celulas: celulasNormalizadas,
			tem_itens:
				linha?.tem_itens ??
				celulasNormalizadas.some((celula) => celula.tem_itens),
		};
	}

	return {
		...linha,
		sala: linha?.sala || null,
		faixa_horaria: linha?.faixa_horaria || {
			codigo: 'DIURNO',
			nome: 'Diurno',
		},
		celulas: (colunas || []).map((coluna = {}) => ({
			data: coluna?.data || '',
			dia_semana: coluna?.dia_semana,
			nome_dia_semana: coluna?.nome_dia_semana || '',
			is_fim_de_semana: coluna?.is_fim_de_semana ?? false,
			quantidade_itens: 0,
			tem_itens: false,
			cards: [],
		})),
		tem_itens: false,
	};
};

const normalizeTrocaCardEscala = (item = {}) => {
	const dados = item?.dados_edicao || {};
	return {
		...item,
		id: item?.id ?? dados?.id ?? null,
		tipo_item: item?.tipo_item || dados?.tipo_item || 'ALOCACAO',
		nome_profissional:
			item?.nome_profissional ||
			dados?.nome ||
			dados?.nome_profissional ||
			item?.resumo?.titulo ||
			'',
		profissional:
			item?.profissional ||
			dados?.profissional ||
			item?.profissional_id ||
			dados?.profissional_id ||
			null,
		vinculo:
			item?.vinculo ||
			dados?.vinculo ||
			item?.vinculo_id ||
			dados?.vinculo_id ||
			dados?.vinculo_tipo ||
			null,
		data: item?.data || dados?.data || '',
		hora_inicio: item?.hora_inicio || dados?.hora_inicio || '',
		hora_fim: item?.hora_fim || dados?.hora_fim || '',
		setor: item?.setor || dados?.setor || null,
		sala: item?.sala || dados?.sala || null,
		resumo: {
			...(item?.resumo || {}),
			titulo:
				item?.resumo?.titulo ||
				item?.nome_profissional ||
				dados?.nome ||
				'',
			especialidade:
				item?.resumo?.especialidade || dados?.especialidade || '',
			vinculo:
				item?.resumo?.vinculo ||
				dados?.descricao_vinculo ||
				dados?.vinculo ||
				'',
		},
		dados_edicao: {
			...dados,
			id: dados?.id ?? item?.id ?? null,
			data: dados?.data || item?.data || '',
			hora_inicio: dados?.hora_inicio || item?.hora_inicio || '',
			hora_fim: dados?.hora_fim || item?.hora_fim || '',
			profissional:
				dados?.profissional ||
				item?.profissional ||
				item?.profissional_id ||
				null,
			vinculo:
				dados?.vinculo || item?.vinculo || dados?.vinculo_id || null,
		},
	};
};

const normalizeConsultaTrocaEscalaPayload = (payload = {}) => {
	const origem = payload?.origem || payload?.item_origem || null;
	const candidatosLegados = unwrapList(payload?.candidatos).map(
		normalizeTrocaCardEscala,
	);
	const candidatosAssistidos = unwrapList(payload?.profissionais).flatMap(
		(profissional = {}) =>
			unwrapList(profissional?.horarios).map((horario = {}) =>
				normalizeTrocaCardEscala({
					...horario,
					nome_profissional:
						profissional?.nome || horario?.nome_profissional || '',
					profissional:
						profissional?.id ||
						horario?.profissional ||
						horario?.profissional_id ||
						null,
					resumo: {
						...(horario?.resumo || {}),
						titulo:
							profissional?.nome ||
							horario?.nome_profissional ||
							horario?.resumo?.titulo ||
							'',
						especialidade:
							horario?.especialidade?.nome ||
							horario?.especialidade ||
							horario?.resumo?.especialidade ||
							'',
						vinculo:
							horario?.vinculo?.nome ||
							horario?.vinculo ||
							horario?.resumo?.vinculo ||
							'',
					},
					dados_edicao: {
						...(horario?.dados_edicao || {}),
						id: horario?.item_id || horario?.id || null,
						data:
							horario?.data || horario?.dados_edicao?.data || '',
						hora_inicio:
							horario?.hora_inicio ||
							horario?.dados_edicao?.hora_inicio ||
							'',
						hora_fim:
							horario?.hora_fim ||
							horario?.dados_edicao?.hora_fim ||
							'',
					},
				}),
			),
	);

	return {
		...payload,
		origem: origem ? normalizeTrocaCardEscala(origem) : null,
		item_origem: origem ? normalizeTrocaCardEscala(origem) : null,
		profissionais: unwrapList(payload?.profissionais),
		facetas: payload?.facetas || {},
		candidatos:
			candidatosAssistidos.length > 0
				? candidatosAssistidos
				: candidatosLegados,
	};
};

const normalizeOperacaoEscalaResponse = (payload = {}) => {
	if (payload?.visualizacao || payload?.grade) {
		return normalizeBuscarGradeEscalaPayload(payload);
	}

	return payload;
};

const normalizeFormularioOperacaoPayload = (payload = {}) => ({
	...payload,
	item: payload?.item || null,
	contexto_competencia:
		payload?.contexto_competencia || payload?.contextoCompetencia || null,
	defaults: payload?.defaults || {},
	catalogos: {
		...(payload?.catalogos || {}),
		tipos_item: unwrapList(
			payload?.catalogos?.tipos_item || payload?.catalogos?.tiposItem,
		),
		categorias: unwrapList(payload?.catalogos?.categorias || []),
		especialidades: unwrapList(payload?.catalogos?.especialidades || []),
		vinculos: unwrapList(payload?.catalogos?.vinculos || []),
		setores_relacionados: unwrapList(
			payload?.catalogos?.setores_relacionados ||
				payload?.catalogos?.setoresRelacionados ||
				payload?.catalogos?.setores ||
				[],
		).map((setor) => normalizeSetorCatalogo(setor)),
	},
	acoes: payload?.acoes || {},
});

const normalizeBuscarGradeEscalaPayload = (payload = {}) => {
	const visualizacao = payload?.grade || payload?.visualizacao || {};
	const colunas = unwrapList(visualizacao?.colunas).map((coluna = {}) => ({
		...coluna,
		data: coluna?.data || '',
		nome_dia_semana: coluna?.nome_dia_semana || '',
	}));

	const setores = unwrapList(visualizacao?.setores).map(
		(setor = {}, index) => {
			const setorNormalizado = {
				...setor,
				id: setor?.id ?? index + 1,
				nome: setor?.nome || setor?.nome_exibicao || 'Setor',
				nome_exibicao: setor?.nome_exibicao || setor?.nome || 'Setor',
				identificador:
					setor?.identificador ||
					setor?.codigo ||
					setor?.codigo_identificador ||
					payload?.contexto?.identificador ||
					payload?.filtros_aplicados?.identificador ||
					'',
				nome_tipo_secao:
					setor?.nome_tipo_secao ||
					payload?.contexto?.tipo_secao?.nome ||
					payload?.filtros_aplicados?.tipo_secao_nome ||
					payload?.filtros?.nome_tipo_secao ||
					payload?.filtros?.tipo_secao_nome ||
					'',
				tipo_secao:
					setor?.tipo_secao ||
					payload?.contexto?.tipo_secao?.id ||
					payload?.filtros_aplicados?.tipo_secao ||
					payload?.filtros?.tipo_secao ||
					null,
				quantidade_leitos:
					setor?.quantidade_leitos ??
					setor?.numero_leitos ??
					payload?.contexto?.quantidade_leitos ??
					payload?.resumo?.quantidade_leitos ??
					0,
				linhas: [],
			};

			setorNormalizado.linhas = unwrapList(setor?.linhas).map(
				(linha = {}, linhaIndex) => ({
					linha_id:
						linha?.linha_id ||
						`setor-${setorNormalizado.id}-linha-${linhaIndex + 1}`,
					ordem: linha?.ordem ?? linhaIndex + 1,
					...normalizeLinhaEscala(linha, colunas, setorNormalizado),
				}),
			);

			return setorNormalizado;
		},
	);

	const itens = setores.flatMap((setor) =>
		(setor?.linhas || []).flatMap((linha) =>
			(linha?.celulas || []).flatMap((celula) => celula?.cards || []),
		),
	);

	return {
		...payload,
		ultima_atualizacao:
			payload?.ultima_atualizacao ||
			payload?.resumo?.ultima_atualizacao ||
			payload?.escala?.ultima_atualizacao ||
			payload?.contexto?.ultima_atualizacao ||
			null,
		itens,
		escala: payload?.escala
			? {
					...payload.escala,
					id: payload?.escala?.id || null,
					identificador:
						payload?.escala?.identificador ||
						payload?.contexto?.identificador ||
						payload?.filtros_aplicados?.identificador ||
						payload?.escala?.codigo ||
						null,
					quantidade_leitos:
						payload?.escala?.quantidade_leitos ??
						payload?.contexto?.quantidade_leitos ??
						payload?.resumo?.quantidade_leitos ??
						null,
					regime:
						payload?.escala?.regime ||
						payload?.contexto?.regime ||
						payload?.filtros_aplicados?.regime ||
						payload?.filtros?.regime ||
						'PLANTONISTA',
					mes_referencia:
						payload?.escala?.mes_referencia ||
						payload?.contexto?.mes_referencia ||
						payload?.mes_referencia ||
						payload?.filtros?.mes_referencia ||
						null,
					ultima_atualizacao:
						payload?.escala?.ultima_atualizacao ||
						payload?.contexto?.ultima_atualizacao ||
						payload?.ultima_atualizacao ||
						null,
				}
			: setores.length > 0
				? {
						id: null,
						virtual: true,
						identificador:
							payload?.contexto?.identificador ||
							payload?.filtros_aplicados?.identificador ||
							setores?.[0]?.identificador ||
							null,
						quantidade_leitos:
							payload?.contexto?.quantidade_leitos ??
							payload?.resumo?.quantidade_leitos ??
							setores?.[0]?.quantidade_leitos ??
							null,
						nome_servico:
							setores[0]?.nome_tipo_secao ||
							payload?.contexto?.tipo_secao?.nome ||
							payload?.filtros?.servico ||
							'Tipo de setor',
						regime:
							payload?.contexto?.regime ||
							payload?.filtros_aplicados?.regime ||
							payload?.filtros?.regime ||
							'PLANTONISTA',
						mes_referencia:
							payload?.contexto?.mes_referencia ||
							payload?.mes_referencia ||
							payload?.filtros?.mes_referencia ||
							null,
						ultima_atualizacao:
							payload?.contexto?.ultima_atualizacao ||
							payload?.ultima_atualizacao ||
							null,
					}
				: null,
		grade: {
			...visualizacao,
			setor_inicial_id:
				visualizacao?.setor_inicial_id || setores?.[0]?.id || null,
			colunas,
			setores,
		},
		visualizacao: {
			...visualizacao,
			setor_inicial_id:
				visualizacao?.setor_inicial_id || setores?.[0]?.id || null,
			colunas,
			setores,
		},
	};
};

export const requestNewUnits = async ({ token } = {}) => {
	try {
		const { data } = await axios.get(`${currentUrl}/unidades/`, {
			...authHeaders(token),
		});
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao buscar unidades.');
	}
};

export const requestBuscarGradeEscala = async ({
	token,
	signal,
	estabelecimento,
	servico,
	tipoSecao,
	identificador,
	setor,
	regime,
	mes,
	ano,
	nomeProfissional,
	profissionalId,
	especialidade,
	tipoItem,
	incluirSugestoesHistoricas,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('escalas-mensais/buscar-grade/', scope),
			{
				params: compactParams({
					estabelecimento,
					servico: servico || undefined,
					tipo_secao: tipoSecao || servico || undefined,
					identificador: identificador || undefined,
					setor: setor || undefined,
					regime: regime || undefined,
					mes,
					ano,
					nome_profissional: nomeProfissional || undefined,
					profissional_id: profissionalId || undefined,
					especialidade: especialidade || undefined,
					tipo_item: tipoItem || undefined,
					incluir_sugestoes_historicas: incluirSugestoesHistoricas,
				}),
				...authHeaders(token),
				signal,
			},
		);
		return normalizeBuscarGradeEscalaPayload(data);
	} catch (error) {
		return buildError(error, 'Erro ao buscar grade de escala.');
	}
};

export const requestBuscarGradeEspecialidadeEscala = async ({
	token,
	signal,
	estabelecimento,
	especialidade,
	regime,
	mes,
	ano,
	nomeProfissional,
	profissionalId,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(
				'escalas-mensais/buscar-grade-especialidade/',
				scope,
			),
			{
				params: compactParams({
					estabelecimento,
					especialidade: especialidade || undefined,
					regime: regime || undefined,
					mes,
					ano,
					nome_profissional: nomeProfissional || undefined,
					profissional_id: profissionalId || undefined,
				}),
				...authHeaders(token),
				signal,
			},
		);
		const calendarios = Array.isArray(data?.calendarios)
			? data.calendarios
			: Array.isArray(data)
				? data
				: [];
		return {
			calendarios: calendarios.map(normalizeBuscarGradeEscalaPayload),
		};
	} catch (error) {
		return buildError(error, 'Erro ao buscar grade por especialidade.');
	}
};

export const requestCatalogosEscalaSelects = async ({
	token,
	signal,
	estabelecimento,
	somenteComItens = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('escalas-mensais/filtros-busca/', scope),
			{
				params: compactParams({
					estabelecimento,
					somente_com_itens: somenteComItens || undefined,
				}),
				...authHeaders(token),
				signal,
			},
		);
		return normalizeCatalogosEscalaSelects(data);
	} catch (error) {
		return buildError(error, 'Erro ao carregar catálogos da escala.');
	}
};

export const requestOpcoesAmbientePreenchimentoEscala = async ({
	token,
	signal,
	contexto,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/opcoes/',
				scope,
			),
			{
				params: buildAmbienteContextoParams(contexto),
				...authHeaders(token),
				signal,
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao carregar opções do ambiente assistido da escala.',
		);
	}
};

export const requestAbrirAmbientePreenchimentoEscala = async ({
	token,
	signal,
	contexto,
	modo = 'detalhado',
	incluirCards,
	incluirProfissionaisSemCards,
	scope,
	forceRefresh = false,
}) => {
	try {
		const requestConfig = {
			// Evite Cache-Control/Pragma: esses cabeçalhos não são simples e a
			// API não libera o preflight CORS correspondente. A chave de leitura
			// não tem efeito de domínio e só diferencia uma releitura pós-mutation.
			params: {
				...buildAmbienteContextoParams(contexto, modo),
				...(incluirCards !== undefined ? { incluir_cards: incluirCards } : {}),
				...(incluirProfissionaisSemCards !== undefined ? { incluir_profissionais_sem_cards: incluirProfissionaisSemCards } : {}),
				...(forceRefresh ? { _refresh: Date.now() } : {}),
			},
			...authHeaders(token),
				signal,
		};
		const { data } = await axios.get(
			buildEscalaUrl('escalas-mensais/ambiente-preenchimento/', scope),
			{
				// A API recebe o contexto habitual; `_refresh` é ignorado pelo
				// servidor e existe exclusivamente para evitar cache do navegador
				// depois de uma mutação já confirmada.
				...requestConfig,
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao abrir ambiente assistido da escala.');
	}
};

// O ambiente de alocação de origem contém a regra estrutural completa
// (padrões da origem e todas as partições). O resumo mensal comum contém
// projeções/cards e não deve ser usado para reconstruir um recorte.
export const requestAbrirAlocacaoOrigemAmbienteEscala = async ({
	token,
	signal,
	contexto,
	alocacaoOrigemId,
	vinculoProfissionalId,
	scope,
	forceRefresh = false,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/alocacao-origem/',
				scope,
			),
			{
				// Este endpoint não recebe setor nem modo. Ele aceita somente a
				// competência, o estabelecimento e os seletores canônicos de
				// origem/contrato; o contrato permite carregar suas origens em lote.
				params: compactParams({
					estabelecimento: contexto?.estabelecimento,
					mes_referencia: normalizeMesReferencia(contexto?.mes_referencia),
					alocacao_origem_id:
						alocacaoOrigemId || contexto?.alocacao_origem_id || undefined,
					vinculo_profissional_id:
						vinculoProfissionalId ?? contexto?.vinculo_profissional_id,
					// A leitura estrutural é usada para montar a versão e os UUIDs que
					// serão enviados ao preview. Uma resposta 304 não traz corpo para
					// o axios e interrompe o fluxo antes da prévia; esta chave é
					// ignorada pela API e evita a revalidação do cache quando solicitada.
					_refresh: forceRefresh ? Date.now() : undefined,
				}),
				...authHeaders(token),
				signal,
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao abrir o estado de alocação de origem da escala.',
		);
	}
};

// A linha pode ser MANUAL e não possuir regime. Os seletores são alternativos;
// nunca consultar o detalhe sem identidade nem escolher um deles silenciosamente.
export const requestDetalharLinhaAmbienteEscala = async ({
	token,
	signal,
	contexto,
	regimeVinculoId,
	linhaId,
	scope,
}) => {
	try {
		const temRegime = regimeVinculoId != null && String(regimeVinculoId) !== '';
		const temLinha = linhaId != null && String(linhaId) !== '';
		if (temRegime === temLinha) {
			throw integrationError(
				'SELETOR_LINHA_INVALIDO',
				'Informe somente o identificador da linha ou do regime para consultar o detalhe.',
			);
		}
		const { data } = await axios.get(
			buildEscalaUrl('escalas-mensais/ambiente-preenchimento/linha/', scope),
			{
				...authHeaders(token),
				signal,
				params: compactParams({
					...buildAmbienteContextoParams(contexto),
					regime_vinculo_id: temRegime ? regimeVinculoId : undefined,
					linha_id: temLinha ? linhaId : undefined,
				}),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao consultar o detalhe da linha da escala.');
	}
};

// Planejamento assistido é deliberadamente uma leitura/decisão guiada pelo
// servidor. O cliente encaminha a intenção e os comandos HATEOAS devolvidos,
// mas não recompõe ciclos, carga ou partições.
export const requestPlanejamentoProfissionalEnfermagem = async ({
	token,
	signal,
	estabelecimento,
	mesReferencia,
	vinculoProfissionalId,
	dataInicio,
	dataFim,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/planejamento-profissional/',
				'enfermagem',
			),
			{
				params: compactParams({
					estabelecimento,
					mes_referencia: normalizeMesReferencia(mesReferencia),
					vinculo_profissional_id: vinculoProfissionalId,
					data_inicio: dataInicio,
					data_fim: dataFim,
				}),
				...authHeaders(token),
				signal,
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao carregar o planejamento assistido do profissional.',
		);
	}
};

export const requestExecutarAcaoPlanejamentoEnfermagem = async ({
	token,
	action,
	body,
	headers = {},
}) => {
	try {
		const requestConfig = authHeaders(token);
		requestConfig.headers = {
			...requestConfig.headers,
			...getActionDeclaredHeaders(action),
			...headers,
		};
		const method = getActionMethod(action);
		const url = buildEscalaActionUrl({
			href: action?.href,
			scope: 'enfermagem',
			fallbackPath:
				'escalas-mensais/ambiente-preenchimento/planejamento-profissional/sugerir/',
		});
		const response = await executeActionRequest(url, method, body ?? action?.entrada, requestConfig);
		return {
			...response.data,
			meta_http: {
				idempotency_replayed:
					String(
						response?.headers?.['idempotency-replayed'] ||
							response?.headers?.['Idempotency-Replayed'] ||
							'false',
					).toLowerCase() === 'true',
			},
		};
	} catch (error) {
		return buildError(error, 'Erro ao executar a ação de planejamento.');
	}
};

export const requestSalvarProfissionalAmbienteEscala = async ({
	token,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/adicionar-profissional/',
				scope,
			),
			normalizeContextoBodyEstabelecimento(body),
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao salvar profissional no ambiente assistido.',
		);
	}
};

// Cadastro e alocação são etapas distintas. Este endpoint cria somente o
// profissional/vínculo institucional; setor, regime e competência pertencem
// à jornada de origem, que é prevista e aplicada em seguida pelo cliente.
export const requestCadastrarVinculoProfissionalEstabelecimento = async ({
	token,
	body,
	scope,
}) => {
	try {
		const { contexto: _contexto, ...cadastro } = body || {};
		const { data } = await axios.post(
			buildEscalaUrl('vinculos-profissionais-estabelecimento/', scope),
			{
				...cadastro,
				estabelecimento: normalizeEstabelecimentoParam(
					cadastro?.estabelecimento,
				),
			},
			authHeaders(token),
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao cadastrar o vÃ­nculo profissional no estabelecimento.',
		);
	}
};

export const requestDetalharVinculoProfissionalEstabelecimento = async ({
	token,
	id,
	scope,
	signal,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(
				`vinculos-profissionais-estabelecimento/${id}/`,
				scope,
			),
			{
				...authHeaders(token),
				signal,
			},
		);
		const cadastro = data?.vinculo_profissional || data || {};
		const profissional =
			cadastro?.profissional && typeof cadastro.profissional === 'object'
				? cadastro.profissional
				: {};
		return {
			...cadastro,
			vinculo_profissional_id:
				cadastro.vinculo_profissional_id || cadastro.id || Number(id),
			profissional_id:
				cadastro.profissional_id || profissional.id ||
				(typeof cadastro.profissional === 'number' ? cadastro.profissional : undefined),
			cpf:
				cadastro.cpf_profissional || profissional.cpf || cadastro.cpf || '',
			nome:
				cadastro.nome || cadastro.nome_profissional || profissional.nome || '',
			conselho:
				cadastro.conselho || cadastro.conselho_profissional || profissional.conselho || '',
			numero_registro:
				cadastro.numero_registro || cadastro.numero_registro_profissional || profissional.numero_registro || '',
			uf_conselho:
				cadastro.uf_conselho || cadastro.uf_conselho_profissional || profissional.uf_conselho || '',
			telefone:
				cadastro.telefone || cadastro.telefone_profissional || profissional.telefone || '',
			vinculo_tipo_id:
				cadastro.vinculo_tipo_id || cadastro.tipo_vinculo_id ||
				(typeof cadastro.vinculo === 'object' ? cadastro.vinculo?.id : cadastro.vinculo) || '',
		};
	} catch (error) {
		return buildError(
			error,
			'Erro ao carregar o vínculo profissional do estabelecimento.',
		);
	}
};

export const requestAdicionarProfissionalAmbienteEscala =
	requestSalvarProfissionalAmbienteEscala;

export const requestAdicionarVacanciaAmbienteEscala = async ({
	token,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/adicionar-vacancia/',
				scope,
			),
			normalizeContextoBodyEstabelecimento(body),
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao adicionar vacância no ambiente assistido.',
		);
	}
};

export const requestPreverPreenchimentoAmbienteEscala = async ({
	token,
	body,
	scope,
}) => {
	try {
		const response = await axios.post(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/prever-preenchimento/',
				scope,
			),
			normalizeContextoBodyEstabelecimento(body),
			{
				...authHeaders(token),
			},
		);
		const previewEtag =
			response?.data?.preview_etag ||
			response?.data?.preview?.preview_etag ||
			response?.headers?.etag ||
			response?.headers?.ETag ||
			'';
		return previewEtag
			? { ...response.data, preview_etag: previewEtag }
			: response.data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao prever preenchimento do ambiente assistido.',
		);
	}
};

export const requestAplicarPreenchimentoAmbienteEscala = async ({
	token,
	body,
	action,
	scope,
}) => {
	try {
		const { payload, config: requestConfig } = buildApplyRequest({ token, action, body });

		const { data } = await axios({
			method: getActionMethod(action),
			url: buildEscalaActionUrl({
				href: action?.href,
				scope,
				fallbackPath:
					'escalas-mensais/ambiente-preenchimento/aplicar-preenchimento/',
			}),
			data: payload,
			...requestConfig,
		});
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao aplicar preenchimento do ambiente assistido.',
		);
	}
};

// Particionamento usa uma alocação de origem para manter a jornada única e
// distribuir somente faixas dela entre os setores. Não use o fluxo legado de
// criar outro vínculo para esse caso, pois ele trata as faixas coincidentes
// como conflito de profissional.
export const requestPreverParticionamentoAlocacaoOrigem = async ({
	token,
	body,
	scope,
}) => {
	try {
		const response = await axios.post(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/alocacao-origem/prever/',
				scope,
			),
			normalizeContextoBodyEstabelecimento(body),
			authHeaders(token),
		);
		const previewEtag =
			response?.data?.preview_etag ||
			response?.data?.preview?.preview_etag ||
			response?.headers?.etag ||
			response?.headers?.ETag ||
			'';
		return previewEtag
			? { ...response.data, preview_etag: previewEtag }
			: response.data;
	} catch (error) {
		return buildError(error, 'Erro ao prever o particionamento da jornada.');
	}
};

// A edição de uma origem é dirigida por HATEOAS. A API pode devolver uma
// ação de versionamento estrutural ou uma nova sugestão assistida, portanto o
// cliente deve seguir a rota e o corpo-base devolvidos em `acoes.editar`.
export const requestExecutarAcaoEdicaoAlocacaoOrigem = async ({
	token,
	action,
	body,
	headers = {},
	scope,
}) => {
	try {
		const method = getActionMethod(action);
		const url = buildEscalaActionUrl({
			href: action?.href,
			scope,
			fallbackPath:
				'escalas-mensais/ambiente-preenchimento/alocacao-origem/prever/',
		});
		const requestConfig = authHeaders(token);
		requestConfig.headers = {
			...requestConfig.headers,
			...getActionDeclaredHeaders(action),
			...headers,
		};
		const response = await executeActionRequest(
			url, method, normalizeContextoBodyEstabelecimento(body ?? action?.entrada), requestConfig,
		);
		return response.data;
	} catch (error) {
		return buildError(error, 'Erro ao preparar a edição da jornada de origem.');
	}
};

export const requestAplicarParticionamentoAlocacaoOrigem = async ({
	token,
	body,
	previewEtag,
	idempotencyKey,
	action,
	scope,
}) => {
	try {
		const { payload, config: requestConfig } = buildApplyRequest({ token, action, body, previewEtag, idempotencyKey });

		const { data } = await axios({
			method: getActionMethod(action),
			url: buildEscalaActionUrl({
				href: action?.href,
				scope,
				fallbackPath:
					'escalas-mensais/ambiente-preenchimento/alocacao-origem/aplicar/',
			}),
			data: payload,
			...requestConfig,
		});
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao aplicar o particionamento da jornada.');
	}
};

export const requestPreverAcoesAmbienteEscala = async ({
	token,
	body,
	scope,
}) => {
	try {
		const response = await axios.post(
			buildEscalaUrl(
				'escalas-mensais/ambiente-preenchimento/prever-acoes/',
				scope,
			),
			normalizeContextoBodyEstabelecimento(body),
			{
				...authHeaders(token),
			},
		);
		const previewEtag =
			response?.data?.preview_etag ||
			response?.data?.preview?.preview_etag ||
			response?.headers?.etag ||
			response?.headers?.ETag ||
			'';
		return previewEtag
			? { ...response.data, preview_etag: previewEtag }
			: response.data;
	} catch (error) {
		return buildError(error, 'Erro ao prever ações do ambiente assistido.');
	}
};

export const requestAplicarAcoesAmbienteEscala = async ({
	token,
	body,
	action,
	scope,
}) => {
	try {
		const { payload, config: requestConfig } = buildApplyRequest({ token, action, body });

		const { data } = await axios({
			method: getActionMethod(action),
			url: buildEscalaActionUrl({
				href: action?.href,
				scope,
				fallbackPath:
					'escalas-mensais/ambiente-preenchimento/aplicar-acoes/',
			}),
			data: payload,
			...requestConfig,
		});
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao aplicar ações do ambiente assistido.',
		);
	}
};

export const requestFormularioNovoItemEscala = async ({
	token,
	estabelecimento,
	setor,
	tipoSecao,
	identificador,
	regime,
	mes,
	ano,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('itens-escala/formulario-novo/', scope),
			{
				params: compactParams({
					estabelecimento,
					setor: setor || undefined,
					tipo_secao: tipoSecao || undefined,
					identificador: identificador || undefined,
					regime: regime || undefined,
					mes: mes || undefined,
					ano: ano || undefined,
				}),
				...authHeaders(token),
			},
		);
		return normalizeFormularioOperacaoPayload(data);
	} catch (error) {
		return buildError(error, 'Erro ao carregar formulário de novo item.');
	}
};

export const requestFormularioItemEscala = async ({ token, id, scope, signal }) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(`itens-escala/${id}/formulario/`, scope),
			{
				...authHeaders(token),
				signal,
			},
		);
		return normalizeFormularioOperacaoPayload(data);
	} catch (error) {
		return buildError(error, 'Erro ao carregar formulário de edição.');
	}
};

export const requestVinculosEscala = async ({ token, scope, signal }) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('vinculos-escala/', scope),
			{
				...authHeaders(token),
				signal,
			},
		);
		return unwrapList(data);
	} catch (error) {
		return buildError(error, 'Erro ao carregar vínculos.');
	}
};

export const requestBuscarProfissionalEscala = async ({
	token,
	signal,
	cpf,
	termo,
	estabelecimento,
	vinculo,
	dataReferencia,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('profissionais-escala/buscar/', scope),
			{
				params: compactParams({
					termo: termo || (vinculo ? undefined : cpf || undefined),
					cpf: vinculo ? cpf || undefined : undefined,
					estabelecimento,
					vinculo: vinculo || undefined,
					data_referencia: dataReferencia || undefined,
				}),
				...authHeaders(token),
				signal,
			},
		);
		const profissionais = unwrapList(data);
		if (profissionais.length > 0) return profissionais;
		if (data?.id) return [data];
		if (data?.profissional?.id) return [data.profissional];
		return [];
	} catch (error) {
		return buildError(error, 'Erro ao buscar profissional.');
	}
};

export const requestClassificarContratoLegado = async ({ token, scope, action }) => {
	if (action?.disponivel !== true || action?.method !== 'PATCH' || !action?.href || !action?.entrada) {
		return { error: { message: action?.motivo || 'A classificação deste contrato não está disponível.' } };
	}
	try {
		const response = await axios.patch(buildEscalaActionUrl({ href: action.href, scope }), action.entrada, authHeaders(token));
		return response.data;
	} catch (error) {
		return buildError(error, 'Não foi possível classificar o contrato legado.');
	}
};

export const requestConsultarRegimeInstitucional = async ({ token, id, estabelecimento, scope, action, dataReferencia }) => {
	try {
		const response = await axios.get(
			buildEscalaActionUrl({ href: action?.href, scope, fallbackPath: `vinculos-profissionais-estabelecimento/${id}/regimes/` }),
			{ ...authHeaders(token), params: compactParams({ estabelecimento, data_referencia: dataReferencia }) },
		);
		return response.data;
	} catch (error) {
		return buildError(error, 'Não foi possível consultar o regime institucional do contrato.');
	}
};

export const requestExecutarAcaoRegimeInstitucional = async ({ token, scope, action, body, previewEtag, idempotencyKey }) => {
	if (action?.disponivel !== true || !action?.href || action?.method !== 'POST') {
		return { error: { message: action?.motivo || 'A ação institucional não está disponível.' } };
	}
	try {
		const config = authHeaders(token);
		config.headers = {
			...config.headers,
			...getActionDeclaredHeaders(action),
			...(previewEtag ? { 'If-Match': previewEtag } : {}),
			...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
		};
		const response = await axios.post(
			buildEscalaActionUrl({ href: action.href, scope }), body, config,
		);
		return {
			...response.data,
			preview_etag: response.data?.preview_etag || response.headers?.etag || response.headers?.ETag,
			idempotency_replayed: String(response.headers?.['idempotency-replayed'] || response.headers?.['Idempotency-Replayed']) === 'true',
		};
	} catch (error) {
		return buildError(error, 'Não foi possível concluir a operação de regime institucional.');
	}
};

export const requestAtualizarVinculoProfissionalEstabelecimento = async ({
	token,
	id,
	body,
	scope,
}) => {
	try {
		const requestConfig = authHeaders(token);
		if (body?.chave_idempotencia) {
			requestConfig.headers = {
				...requestConfig.headers,
				'Idempotency-Key': body.chave_idempotencia,
			};
		}

		const { data } = await axios.patch(
			buildEscalaUrl(
				`vinculos-profissionais-estabelecimento/${id}/`,
				scope,
			),
			body,
			requestConfig,
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao atualizar vínculo profissional do estabelecimento.',
		);
	}
};

export const requestCriarItemEscala = async ({ token, body, scope }) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('itens-escala/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return normalizeOperacaoEscalaResponse(data);
	} catch (error) {
		return buildError(error, 'Erro ao criar item da escala.');
	}
};

export const requestCriarLoteItensEscala = async ({ token, body, scope, idempotencyKey }) => {
	try {
		const config = authHeaders(token);
		if (idempotencyKey) config.headers['Idempotency-Key'] = idempotencyKey;
		const response = await axios.post(
			buildEscalaUrl('itens-escala/lote/', scope),
			body,
			config,
		);
		const resultado = normalizeOperacaoEscalaResponse(response.data);
		const replay = response.headers?.['idempotency-replayed'] ?? response.headers?.['Idempotency-Replayed'];
		return replay === undefined ? resultado : { ...resultado, idempotency_replayed: String(replay).toLowerCase() === 'true' };
	} catch (error) {
		return buildError(error, 'Erro ao criar lote da escala.');
	}
};

export const requestEditarItemEscala = async ({ token, id, body, scope }) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl(`itens-escala/${id}/`, scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return normalizeOperacaoEscalaResponse(data);
	} catch (error) {
		return buildError(error, 'Erro ao editar item da escala.');
	}
};

export const requestEditarLoteItensEscala = async ({ token, body, scope }) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl('itens-escala/lote/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return normalizeOperacaoEscalaResponse(data);
	} catch (error) {
		return buildError(error, 'Erro ao editar lote da escala.');
	}
};

export const requestConsultarTrocaEscala = async ({ token, id, scope, signal }) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl(`itens-escala/${id}/troca/`, scope),
			{
				...authHeaders(token),
				...(signal !== undefined ? { signal } : {}),
			},
		);
		return normalizeConsultaTrocaEscalaPayload(data);
	} catch (error) {
		return buildError(error, 'Erro ao consultar candidatos para troca.');
	}
};

const buildTrocaEquivalenteParams = ({
	itensA = [],
	itensB = [],
	...filters
} = {}) =>
	compactParams({
		itens_a: itensA.join(','),
		itens_b: itensB.join(','),
		...filters,
	});

export const requestConsultarTrocasEquivalentes = async ({
	token,
	itensA = [],
	itensB = [],
	lado,
	cursor,
	cursorCards,
	profissionalId,
	nomeProfissional,
	data,
	dimensaoId,
	limite,
	body = null,
	scope,
}) => {
	try {
		if (body) {
			const { data: payload } = await axios.post(
				buildEscalaUrl('trocas/consulta-v2/', scope),
				body,
				authHeaders(token),
			);
			return payload;
		}

		const { data: payload } = await axios.get(
			buildEscalaUrl('trocas/consulta-v2/', scope),
			{
				...authHeaders(token),
				params: buildTrocaEquivalenteParams({
					itensA,
					itensB,
					lado,
					cursor,
					cursor_cards: cursorCards,
					profissional_id: profissionalId,
					nome_profissional: nomeProfissional,
					data,
					dimensao_id: dimensaoId,
					limite,
				}),
			},
		);
		return payload;
	} catch (error) {
		return buildError(error, 'Erro ao consultar candidatos para troca equivalente.');
	}
};

const getConsultaTrocaProfissionalKey = (profissional = {}) =>
	String(
		profissional?.id ||
			profissional?.profissional_id ||
			profissional?.vinculo_profissional_id ||
			profissional?.profissional?.id ||
			profissional?.profissional?.cpf ||
			profissional?.cpf ||
			profissional?.nome ||
			'',
	).trim();

const getConsultaTrocaCandidatoKey = (candidato = {}) =>
	String(
		candidato?.item?.id ||
			candidato?.item_id ||
			candidato?.id ||
			candidato?.item?.item_id ||
			'',
	).trim();

const mergeProfissionaisConsultaTroca = (paginas = []) => {
	const profissionais = new Map();
	paginas.forEach((pagina = {}) => {
		(Array.isArray(pagina?.profissionais) ? pagina.profissionais : []).forEach(
			(profissional = {}) => {
				const key = getConsultaTrocaProfissionalKey(profissional);
				if (!key) return;
				const atual = profissionais.get(key) || {};
				const candidatos = new Map();
				[...(atual?.candidatos || []), ...(profissional?.candidatos || [])].forEach(
					(candidato = {}) => {
						const candidatoKey = getConsultaTrocaCandidatoKey(candidato);
						if (candidatoKey) candidatos.set(candidatoKey, candidato);
					},
				);
				profissionais.set(key, {
					...atual,
					...profissional,
					candidatos: Array.from(candidatos.values()),
					cards_truncados: false,
					proximo_cursor_cards: null,
				});
			},
		);
	});
	return Array.from(profissionais.values());
};

const getPaginacaoConsultaTroca = (payload = {}) =>
	payload?.paginacao || payload?.meta?.paginacao || {};

const getCursorCardsConsultaTroca = (profissional = {}) =>
	profissional?.proximo_cursor_cards ||
	profissional?.paginacao?.proximo_cursor_cards ||
	profissional?.meta?.proximo_cursor_cards ||
	null;

// `consulta-v2` pagina profissionais e, em alguns casos, os cards de cada
// profissional. O seletor não pode representar apenas a primeira página: a
// API é a fonte de verdade para todos os candidatos elegíveis.
export const requestConsultarTodasTrocasEquivalentes = async ({
	token,
	itensA = [],
	itensB = [],
	lado,
	profissionalId,
	nomeProfissional,
	data,
	dimensaoId,
	scope,
} = {}) => {
	const paginas = [];
	let cursor = null;
	let primeiraResposta = null;
	const cursoresVisitados = new Set();

	for (;;) {
		const response = await requestConsultarTrocasEquivalentes({
			token,
			itensA,
			itensB,
			lado,
			cursor,
			profissionalId,
			nomeProfissional,
			data,
			dimensaoId,
			scope,
		});
		if (response?.error) return response;
		if (!primeiraResposta) primeiraResposta = response;
		paginas.push(response);

		const paginacao = getPaginacaoConsultaTroca(response);
		const proximoCursor = paginacao?.proximo_cursor;
		if (!paginacao?.tem_proxima_pagina || !proximoCursor) break;
		if (cursoresVisitados.has(String(proximoCursor))) break;
		cursoresVisitados.add(String(proximoCursor));
		cursor = proximoCursor;
	}

	const profissionais = mergeProfissionaisConsultaTroca(paginas);
	const paginasCards = [];
	for (const profissional of profissionais) {
		const id = getConsultaTrocaProfissionalKey(profissional);
		let cursorCards = getCursorCardsConsultaTroca(profissional);
		const cardsTruncados =
			profissional?.cards_truncados === true || Boolean(cursorCards);
		if (!id || !cardsTruncados || !cursorCards) continue;

		const cursoresCardsVisitados = new Set();
		while (cursorCards && !cursoresCardsVisitados.has(String(cursorCards))) {
			cursoresCardsVisitados.add(String(cursorCards));
			const response = await requestConsultarTrocasEquivalentes({
				token,
				itensA,
				itensB,
				lado,
				profissionalId: id,
				cursorCards,
				nomeProfissional,
				data,
				dimensaoId,
				scope,
			});
			if (response?.error) return response;
			paginasCards.push(response);
			const retornoProfissional = (
				Array.isArray(response?.profissionais) ? response.profissionais : []
			).find(
				(item = {}) => getConsultaTrocaProfissionalKey(item) === id,
			);
			cursorCards = getCursorCardsConsultaTroca(retornoProfissional || {});
		}
	}

	return {
		...(primeiraResposta || {}),
		profissionais: mergeProfissionaisConsultaTroca([
			...paginas,
			...paginasCards,
		]),
		paginacao: {
			...getPaginacaoConsultaTroca(primeiraResposta),
			tem_proxima_pagina: false,
			proximo_cursor: null,
		},
	};
};

export const requestSimularTrocaEquivalente = async ({
	token,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('trocas/simular/', scope),
			body,
			authHeaders(token),
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao simular troca equivalente.');
	}
};

export const requestComplementosTrocaEquivalente = async ({
	token,
	itensA = [],
	itensB = [],
	body = null,
	scope,
}) => {
	try {
		if (body) {
			const { data } = await axios.post(
				buildEscalaUrl('trocas/complementos/', scope),
				body,
				authHeaders(token),
			);
			return data;
		}

		const { data } = await axios.get(
			buildEscalaUrl('trocas/complementos/', scope),
			{
				...authHeaders(token),
				params: buildTrocaEquivalenteParams({ itensA, itensB }),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao consultar complementos da troca.');
	}
};

export const requestEfetivarTrocaEquivalente = async ({
	token,
	body,
	action,
	idempotencyKey: providedIdempotencyKey,
	scope,
}) => {
	try {
		const idempotencyKey =
			providedIdempotencyKey ||
			body?.chave_idempotencia ||
			body?.idempotency_key ||
			'';
		const { payload, config: requestConfig } = buildApplyRequest({ token, action, body, idempotencyKey, quotePreview: false });

		const { data } = await executeActionRequest(
			buildEscalaActionUrl({
				href: action?.href,
				scope,
				fallbackPath: 'trocas/',
			}),
			getActionMethod(action),
			payload,
			requestConfig,
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao efetivar troca equivalente.');
	}
};

const getCookie = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);

	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}

	return null;
};

const buildSessionCookieRequestConfig = (headers = {}) => {
	const csrfToken = getCookie('csrftoken');

	return {
		withCredentials: true,
		headers: {
			...authHeaders()?.headers,
			...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
			...headers,
		},
	};
};

export const requestRegistrarTrocaEscala = async ({ token, id, body, scope, idempotencyKey }) => {
	try {
		if (!token && !getCookie('csrftoken')) {
			throw integrationError('missing-session-auth', 'Não foi possível autenticar a troca pela sessão atual.');
		}
		const key = idempotencyKey || body?.chave_idempotencia;
		if (!key) throw integrationError('CHAVE_IDEMPOTENCIA_AUSENTE', 'A troca precisa de uma chave de confirmação. Reabra a operação.');
		const config = token ? authHeaders(token) : buildSessionCookieRequestConfig();
		config.headers['Idempotency-Key'] = key;

		const { data } = await axios.post(
			buildEscalaUrl(`itens-escala/${id}/troca/`, scope),
			body,
			config,
		);

		return normalizeOperacaoEscalaResponse(data);
	} catch (error) {
		return buildError(error, 'Erro ao registrar troca da escala.');
	}
};

const solicitacoesTrocaUrl = (path = '') =>
	`${currentUrl}/escala/solicitacoes-troca/${String(path || '').replace(
		/^\//,
		'',
	)}`;

export const requestMeusPlantoesSolicitacaoTroca = async ({
	token,
	modalidade,
	estabelecimento,
	pageSize = 50,
}) => {
	try {
		const { data } = await axios.get(
			solicitacoesTrocaUrl('meus-plantoes/'),
			{
				...authHeaders(token),
				params: compactParams({
					modalidade,
					estabelecimento,
					page_size: pageSize,
				}),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao consultar seus plantões elegíveis.');
	}
};

export const requestCandidatosSolicitacaoTroca = async ({
	token,
	modalidade,
	itemOrigemId,
	dataInicio,
	dataFim,
	setor,
	busca,
	pageSize = 50,
}) => {
	try {
		const { data } = await axios.get(
			solicitacoesTrocaUrl('candidatos/'),
			{
				...authHeaders(token),
				params: compactParams({
					modalidade,
					item_origem_id: itemOrigemId,
					data_inicio: dataInicio,
					data_fim: dataFim,
					setor,
					busca,
					page_size: pageSize,
				}),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao consultar candidatos para troca.');
	}
};

export const requestCriarSolicitacaoTroca = async ({ token, body }) => {
	try {
		const { data } = await axios.post(
			solicitacoesTrocaUrl(),
			body,
			authHeaders(token),
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao enviar solicitação de troca.');
	}
};

export const requestGerenciamentoSolicitacoesTroca = async ({
	token,
	modalidade,
	estabelecimento,
	status,
	dataInicio,
	dataFim,
	busca,
	ordering = '-data_criacao',
	page = 1,
	pageSize = 25,
}) => {
	try {
		const { data } = await axios.get(
			solicitacoesTrocaUrl('gerenciamento/'),
			{
				...authHeaders(token),
				params: compactParams({
					modalidade,
					estabelecimento,
					status,
					data_inicio: dataInicio,
					data_fim: dataFim,
					busca,
					ordering,
					page,
					page_size: pageSize,
				}),
			},
		);
		return data;
	} catch (error) {
		return buildError(
			error,
			'Erro ao carregar o gerenciamento de solicitações.',
		);
	}
};

export const requestAceitarSolicitacaoTroca = async ({ token, id }) => {
	try {
		const { data } = await axios.post(
			solicitacoesTrocaUrl(`${id}/aceitar/`),
			{},
			authHeaders(token),
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao aceitar solicitação de troca.');
	}
};

export const requestRecusarSolicitacaoTroca = async ({
	token,
	id,
	motivoRecusa,
}) => {
	try {
		const { data } = await axios.post(
			solicitacoesTrocaUrl(`${id}/recusar/`),
			{ motivo_recusa: motivoRecusa },
			authHeaders(token),
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao recusar solicitação de troca.');
	}
};

export const requestExcluirItemEscala = async ({ token, id, scope }) => {
	try {
		const { data } = await axios.delete(
			buildEscalaUrl(`itens-escala/${id}/`, scope),
			token ? authHeaders(token) : buildSessionCookieRequestConfig(),
		);

		return normalizeOperacaoEscalaResponse(data);
	} catch (error) {
		return buildError(error, 'Erro ao excluir item da escala.');
	}
};

export const requestListarServicosEscala = async ({
	token,
	estabelecimento,
	incluirInativos = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('servicos-escala/', scope),
			{
				params: compactParams({
					estabelecimento,
					incluir_inativos: incluirInativos || undefined,
				}),
				...authHeaders(token),
			},
		);
		return unwrapList(data);
	} catch (error) {
		return buildError(error, 'Erro ao listar serviços.');
	}
};

export const requestListarSetoresEscala = async ({
	token,
	servico,
	tipoSecao,
	estabelecimento,
	incluirInativos = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('setores-escala/', scope),
			{
				params: compactParams({
					servico: servico || undefined,
					tipo_secao: tipoSecao || undefined,
					estabelecimento,
					incluir_inativos: false || undefined,
				}),
				...authHeaders(token),
			},
		);
		return unwrapList(data);
	} catch (error) {
		return buildError(error, 'Erro ao listar setores.');
	}
};

export const requestListarSalasEscala = async ({
	token,
	setor,
	estabelecimento,
	incluirInativos = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('salas-escala/', scope),
			{
				params: compactParams({
					setor: setor || undefined,
					estabelecimento,
					incluir_inativos: incluirInativos || undefined,
				}),
				...authHeaders(token),
			},
		);
		return unwrapList(data);
	} catch (error) {
		return buildError(error, 'Erro ao listar salas.');
	}
};

export const requestListarEspecialidadesEscala = async ({
	token,
	estabelecimento,
	incluirInativos = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('especialidades-escala/', scope),
			{
				params: compactParams({
					estabelecimento,
					incluir_inativos: incluirInativos || undefined,
				}),
				...authHeaders(token),
			},
		);
		return unwrapList(data);
	} catch (error) {
		return buildError(error, 'Erro ao listar especialidades.');
	}
};

export const requestCriarServicoEscala = async ({ token, body, scope }) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('servicos-escala/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao criar serviço.');
	}
};

export const requestCriarSetorEscala = async ({ token, body, scope }) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('setores-escala/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao criar setor.');
	}
};

export const requestCriarSalaEscala = async ({ token, body, scope }) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('salas-escala/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao criar sala.');
	}
};

export const requestCriarEspecialidadeEscala = async ({
	token,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.post(
			buildEscalaUrl('especialidades-escala/', scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao criar especialidade.');
	}
};

export const requestAtualizarServicoEscala = async ({
	token,
	id,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl(`servicos-escala/${id}/`, scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao atualizar serviço.');
	}
};

export const requestAtualizarSetorEscala = async ({
	token,
	id,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl(`setores-escala/${id}/`, scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao atualizar setor.');
	}
};

export const requestAtualizarSalaEscala = async ({
	token,
	id,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl(`salas-escala/${id}/`, scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao atualizar sala.');
	}
};

export const requestAtualizarEspecialidadeEscala = async ({
	token,
	id,
	body,
	scope,
}) => {
	try {
		const { data } = await axios.patch(
			buildEscalaUrl(`especialidades-escala/${id}/`, scope),
			body,
			{
				...authHeaders(token),
			},
		);
		return data;
	} catch (error) {
		return buildError(error, 'Erro ao atualizar especialidade.');
	}
};

export const requestCatalogosEscalaAdmin = async ({
	token,
	estabelecimento,
	incluirInativos = false,
	scope,
}) => {
	try {
		const { data } = await axios.get(
			buildEscalaUrl('catalogos-escala-admin/', scope),
			{
				params: compactParams({
					estabelecimento,
					incluir_inativos: incluirInativos,
				}),
				...authHeaders(token),
			},
		);
		return {
			tiposSecao: unwrapList(data?.tipos_secao),
			nomesSetor: unwrapList(data?.nomes_setor),
			setores: unwrapList(data?.setores),
		};
	} catch (error) {
		return buildError(error, 'Erro ao carregar catálogos administrativos.');
	}
};
